import React from 'react';
import styled from 'styled-components';
import { Layout } from '../../components/page-layout';
import HeroImg from '../../assets/blog/wireless-security.png';
import Chart1 from '../../assets/blog/chart 1.png';
import Chart2 from '../../assets/blog/chart 2.png';
import RelatedBolg from '../../components/RelatedBolg';

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 64px 24px 48px 24px;
  font-family: 'Nunito', sans-serif;
`;

const Title = styled.h1`
  font-size: 2.8rem;
  font-weight: 800;
  color: #181818;
  text-align: center;
  margin-bottom: 1.5rem;
`;

const Byline = styled.p`
  text-align: center;
  color: #888;
  font-size: 1rem;
  margin-bottom: 2rem;
`;

const HeroImage = styled.img`
  width: 100%;
  max-width: 700px;
  display: block;
  margin: 0 auto 2rem auto;
  border-radius: 24px;
`;

const ChartImage = styled.img`
  width: calc(100% - 1.5rem);
  max-width: 700px;
  display: block;
  margin: 0 0 1.5rem 1.5rem;
  border-radius: 12px;
  box-sizing: border-box;
`;

const Paragraph = styled.p`
  color: #222;
  font-size: 1.15rem;
  margin-bottom: 1.2rem;
`;

const SectionHeading = styled.h2`
  color: #F87B15;
  font-size: 1.15rem;
  font-weight: 800;
  margin: 2.5rem 0 1rem 0;
`;

const BulletList = styled.ul`
  margin-left: 1.5rem;
  margin-bottom: 1rem;
`;

const Bullet = styled.li`
  font-size: 1.05rem;
  margin-bottom: 0.5rem;
  position: relative;
  padding-left: 1.2em;
  list-style: none;
  span {
    color: #222;
    font-weight: 600;
    margin-right: 0.5em;
  }
`;

const BottomLine = styled.h3`
  font-style: italic;
  color: #F87B15;
  font-size: 1.15rem;
  margin: 2.5rem 0 1rem 0;
`;

const ProductLink = styled.a`
  color: #F87B15;
  font-weight: 800;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

export default function WirelessSecuritySystems() {
  return (
    <Layout showCommonFooter>
      <Container>
        <Title>Wireless Security Systems: Are They Safe Enough?</Title>
        <Byline>By Sensforge Team</Byline>
        <HeroImage src={HeroImg} alt="Wireless Security" />
        <Paragraph>
          Wireless security cameras and systems are booming in popularity—and for good reason. They're easy to install, flexible, and often come packed with smart features. But there's a big question that comes up often: "Are wireless security systems actually safe?"
        </Paragraph>
        <Paragraph>
          Let's break it down: what the real risks are, what security features to look for, and how you can protect your home without sacrificing convenience.
        </Paragraph>
        <SectionHeading>Understanding the Concern</SectionHeading>
        <Paragraph>
          Unlike traditional wired systems that are physically connected, wireless systems rely on your home Wi-Fi network to transmit footage and alerts.
        </Paragraph>
        <Paragraph><b>That means:</b></Paragraph>
        <BulletList>
          <Bullet><span>•</span>Footage is often stored in the cloud or transmitted wirelessly</Bullet>
          <Bullet><span>•</span>Data is vulnerable to interception if not protected</Bullet>
          <Bullet><span>•</span>Devices are only as secure as your home network</Bullet>
        </BulletList>
        <Paragraph>
          So yes—wireless systems come with some security considerations. But that doesn't mean they're inherently unsafe. The key is understanding the risks and choosing the right brand.
        </Paragraph>
        <SectionHeading>Common Wireless Security Risks</SectionHeading>
        <Paragraph>Here are a few of the most talked-about concerns:</Paragraph>
        <BulletList>
          <Bullet><span>•</span><b>Hacking or Unauthorized Access</b><br />If a camera's feed isn't encrypted or secured, it could be intercepted by hackers.</Bullet>
          <Bullet><span>•</span><b>Weak or Reused Passwords</b><br />Many users leave default log-in credentials in place—making devices easy to access remotely.</Bullet>
          <Bullet><span>•</span><b>Cloud Privacy Concerns</b><br />Cloud storage is convenient, but users sometimes worry about who has access to their video footage.</Bullet>
          <Bullet><span>•</span><b>Wi-Fi Interference or Outages</b><br />If your Wi-Fi goes down, your system may stop working—unless it has an SD card.</Bullet>
        </BulletList>
        <SectionHeading>Key Security Features to Look For</SectionHeading>
        <Paragraph>Modern wireless security systems now include built-in safeguards to protect your privacy and keep your footage secure. Here are some features worth looking for:</Paragraph>
        <BulletList>
          <Bullet><span>•</span><b>Encrypted Local Storage</b><br />Many cameras now support SD card encryption, meaning footage is useless if the card is removed and accessed externally. Look for AES-128 or AES-256 encryption standards.</Bullet>
          <Bullet><span>•</span><b>Privacy Shutters</b><br />A physical privacy shutter lets you block the camera lens when you're home or not recording—offering visible, tactile peace of mind. This is especially useful for indoor units.</Bullet>
          <Bullet><span>•</span><b>Two-Factor Authentication (2FA)</b><br />Enabling 2FA on your account helps prevent unauthorized login attempts—even if your password is compromised.</Bullet>
          <Bullet><span>•</span><b>End-to-End Encryption</b><br />Some premium systems encrypt video feeds in transit and at rest, making them unreadable without the proper decryption key.</Bullet>
          <Bullet><span>•</span><b>On-Device AI</b><br />Cameras with on-device motion detection or facial recognition reduce the need to send every frame to the cloud—enhancing both privacy and speed.</Bullet>
        </BulletList>
        <SectionHeading>Network Best Practices for Wireless Safety</SectionHeading>
        <Paragraph>No matter how smart your device is, your home Wi-Fi is still your first line of defense. Here's how to lock it down:</Paragraph>
        <ChartImage src={Chart1} alt="Network Best Practices Table" />
        <Paragraph><b>Local vs. Cloud Storage: What's Safer?</b></Paragraph>
        <ChartImage src={Chart2} alt="Local vs Cloud Storage Table" />
        <Paragraph>Some brands offer both—letting you store sensitive footage locally while backing up critical events in the cloud.</Paragraph>
        <SectionHeading>What We're Doing About It</SectionHeading>
        <Paragraph>
          At Sensforge, we believe security shouldn't come at the cost of convenience. That's why our upcoming smart home products prioritize user privacy:
        </Paragraph>
        <BulletList>
          <Bullet><span>•</span><b>AES-encrypted SD card storage</b> to protect footage</Bullet>
          <Bullet><span>•</span><b>Physical privacy shutters</b> for instant control</Bullet>
          <Bullet><span>•</span><b>Two-factor authentication</b> for account security</Bullet>
          <Bullet><span>•</span><b>On-device AI motion detection</b> to reduce unnecessary uploads</Bullet>
        </BulletList>
        <Paragraph style={{fontSize: '0.98rem', color: '#888', marginBottom: '2rem'}}>
          ( Our indoor pan-tilt cameras and upcoming smart ring system are designed with a "privacy-first" mindset—because peace of mind should feel secure and personal. )
        </Paragraph>
        <BottomLine>Final Takeaway</BottomLine>
        <Paragraph>
          Yes—wireless security systems can be safe.<br />
          But only when paired with the right features, strong passwords, and smart practices.<br />
          By choosing devices with encryption, local storage, and privacy controls—and by securing your home network—you can enjoy the flexibility of wireless without compromising on safety.
        </Paragraph>
        <RelatedBolg currentSlug="wireless-security-systems-are-they-safe-enough" />
      </Container>
    </Layout>
  );
}

